import React, { useMemo } from 'react';
import ClockIcon from '@atlaskit/icon/core/clock';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { Flex, xcss, Text, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl, FormattedMessage } from '@atlassian/jira-intl';
import { useDateTimeFormatter } from '@atlassian/jira-issue-format-date/src/main.tsx';
import { getNoOfDaysSinceUTCDateString } from './getIsDatePastOrDueSoon.tsx';
import messages from './messages.tsx';

type DateWithUrgencyIndicatorProps = {
	value: string;
	formattedDateString?: string | null;
};

export const DateWithUrgencyIndicator = ({
	value,
	formattedDateString,
}: DateWithUrgencyIndicatorProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { formatDateOnly } = useDateTimeFormatter();

	const daysBetweenTodayAndValue = useMemo(() => getNoOfDaysSinceUTCDateString(value), [value]);
	const formattedDate = useMemo(
		() => formattedDateString || formatDateOnly(value),
		[formattedDateString, formatDateOnly, value],
	);

	const dueMessage =
		daysBetweenTodayAndValue === 0 ? (
			formatMessage(messages.dueToday)
		) : (
			<FormattedMessage {...messages.dueIn} values={{ daysBetween: daysBetweenTodayAndValue }} />
		);

	const isDatePast = daysBetweenTodayAndValue != null && daysBetweenTodayAndValue >= 0;
	const Icon = daysBetweenTodayAndValue === 0 ? ClockIcon : StatusWarningIcon;

	return (
		<Flex alignItems="center" testId="date-with-urgency-indicator.container" gap="space.075">
			<Box xcss={[baseStyles, isDatePast ? redBackgroundStyle : false]}>
				<Text color={isDatePast ? 'color.text.accent.red' : 'color.text.accent.gray.bolder'}>
					{formattedDate}
				</Text>
			</Box>
			{isDatePast && (
				<Tooltip content={dueMessage} position="top">
					<Icon
						LEGACY_size="medium"
						label=""
						color={token('color.icon.danger')}
						testId="date-with-urgency-indicator.warning-icon"
					/>
				</Tooltip>
			)}
		</Flex>
	);
};

const baseStyles = xcss({
	padding: 'space.025',
	borderRadius: 'radius.small',
});

const redBackgroundStyle = xcss({
	backgroundColor: 'color.background.accent.red.subtlest',
	':hover': {
		backgroundColor: 'color.background.accent.red.subtlest.hovered',
	},
	minWidth: '92px',
});
